import { ReactElement } from "react";
import { createPortal } from "react-dom";

import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";

import styles from "./FlowsheetWarningPopup.module.scss";

/**
 * The properties for a {@link FlowsheetWarningPopup} component.
 */
interface IFlowsheetWarningPopupProps {
    /**
     * A callback invoked when leaving the deleted flowsheet.
     */
    onConfirm: () => void;

    /**
     * Text explaining the error to display in the component
     */
    errorMessages: string[];
}

/**
 * A popup that warns that the flowsheet was deleted.
 * @param props The FlowsheetWarningPopup properties.
 */
function FlowsheetWarningPopup({ errorMessages, onConfirm }: Readonly<IFlowsheetWarningPopupProps>): ReactElement {
    const { getText } = useTranslation();

    return (
        // Portal is required to prevent the parent from scrolling when focusing something in the modal
        createPortal(
            <Modal
                title={getText("Flowsheets_WarningLabel")}
                showX
                disableAutoScroll
                footer={(
                    <div className={styles.flowsheetWarningPopupFooter}>
                        <ButtonControl
                            variant="brand"
                            onClick={onConfirm}
                            label={cacheStore.GetString("OKLabel")}
                        />
                    </div>
                )}
                disableOverlayClose
                onClose={onConfirm}
            >
                {errorMessages.map(p => (
                    <p
                        className={styles.flowsheetWarningPopupMessage}
                        key={`flowsheet-warning-popup-${p}`}
                    >
                        {p}
                    </p>
                ))}
            </Modal>,
            document.body
        )
    );
}

export { FlowsheetWarningPopup };
